import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormField } from '@angular/forms/signals';
import { RouterLink } from '@angular/router';
import jsPDF from 'jspdf';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { AlertaService } from '../../../../shared/utils/alerta.service';
import { LIMITE_OBSERVACOES, SolicitacaoServicoStore } from '../solicitacao-servico.store';
import { LinhaComprovante, dataHora, linhasComprovanteSolicitacao } from './comprovante-solicitacao.util';

/** Medidas do comprovante em milímetros — as mesmas do comprovante da Ouvidoria. */
const MARGEM = 16;
const ALTURA_FAIXA = 26;
const ALTURA_LINHA = 5.5;
const LARGURA_ROTULO = 44;
const RODAPE = 280;

/**
 * A etapa Confirmação, em dois modos:
 *
 * - **revisão** — antes do envio: o resumo do que foi preenchido, as observações e a declaração
 *   que libera o "Concluir". Nada aqui é valor de exemplo: vem do store e do formulário do
 *   serviço;
 * - **recibo** — depois do 201: o protocolo devolvido pela API, a situação, o agendamento e o
 *   comprovante em PDF.
 *
 * O envio em si é do componente pai, que tem os botões de navegação.
 */
@Component({
  selector: 'app-etapa-confirmacao',
  templateUrl: './etapa-confirmacao.component.html',
  styleUrls: ['./etapa-confirmacao.component.css'],
  imports: [LucideModule, FormField, RouterLink],
})
export class EtapaConfirmacaoComponent {
  private readonly alerta = inject(AlertaService);
  private readonly auth = inject(AuthService);
  protected readonly store = inject(SolicitacaoServicoStore);

  private readonly usuario = toSignal(this.auth.currentUser, { initialValue: this.auth.currentUserValue });

  /**
   * Na revisão, o nome da sessão — é o mesmo que o back-end lê do token. Depois do envio vale o
   * que a API devolveu, no recibo.
   */
  private readonly cidadao = computed(() =>
    this.store.anonimo() ? 'Anônimo' : this.usuario()?.nome?.trim() || 'Identificado pelo login',
  );

  readonly servico = input('');

  /** Pedido para o fluxo recomeçar; quem reinicia o wizard é o componente pai. */
  readonly novaSolicitacao = output<void>();

  readonly confirmacaoForm = this.store.solicitacaoForm;

  readonly limiteObservacoes = LIMITE_OBSERVACOES;

  readonly resposta = this.store.respostaAbertura;

  readonly exigeAgendamento = this.store.exigeAgendamento;

  readonly resumo = computed<readonly LinhaComprovante[]>(() => {
    const dados = this.store.dados();
    const linhas: LinhaComprovante[] = [
      { rotulo: 'Serviço:', valor: this.servico() },
      { rotulo: 'Cidadão:', valor: this.cidadao() },
      { rotulo: 'Endereço:', valor: enderecoEmLinha(dados) },
    ];

    if (this.store.permiteSegundaVia()) {
      linhas.push({
        rotulo: 'Tipo do pedido:',
        valor: this.store.segundaVia() ? 'Segunda via' : 'Primeira solicitação',
      });
    }

    if (this.exigeAgendamento()) {
      const local = this.store.formulario()?.localAtendimento;
      const agendamento = this.store.agendamento();

      linhas.push({
        rotulo: 'Posto:',
        valor: [local?.desNomeOrg, local?.desEndereco].filter((parte) => !!parte).join(' — ') || '—',
      });
      linhas.push({ rotulo: 'Data:', valor: agendamento ? formatarDataIso(agendamento.data) : '—' });
      linhas.push({ rotulo: 'Horário:', valor: agendamento?.horario ?? '—' });
    }

    const quantidade = this.store.arquivos().length + Object.keys(this.store.arquivosCampos()).length;

    linhas.push({ rotulo: 'Anexos:', valor: quantidade === 1 ? '1 arquivo' : `${quantidade} arquivos` });

    return linhas;
  });

  readonly linhasRecibo = computed(() => {
    const resposta = this.resposta();

    return resposta ? linhasComprovanteSolicitacao(resposta) : [];
  });

  readonly caracteresObservacoes = computed(() => this.store.dados().observacoes.length);

  alternarDeclaracao(evento: Event): void {
    this.store.declaracaoAceita.set((evento.target as HTMLInputElement).checked);
  }

  /** Monta o comprovante e entrega o arquivo ao navegador. */
  salvarPdf(): void {
    const resposta = this.resposta();

    if (!resposta) return;

    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      this.desenharComprovante(doc);
      doc.save(`comprovante-solicitacao-${resposta.desProtocoloSsv}.pdf`);
    } catch (erro) {
      console.error('Erro ao gerar o comprovante:', erro);

      this.alerta.erro({
        titulo: 'Não foi possível gerar o comprovante',
        texto: 'Tente novamente em instantes.',
      });
    }
  }

  private desenharComprovante(doc: jsPDF): void {
    const larguraPagina = doc.internal.pageSize.getWidth();
    const larguraUtil = larguraPagina - MARGEM * 2;
    const resposta = this.resposta()!;

    doc.setFillColor(11, 36, 70);
    doc.rect(0, 0, larguraPagina, ALTURA_FAIXA, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Comprovante de solicitação de serviço', MARGEM, 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Carta de Serviços', MARGEM, 19);

    let y = ALTURA_FAIXA + 14;

    y = this.desenharTitulo(doc, 'Número do protocolo', y);

    doc.setTextColor(43, 93, 150);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(resposta.desProtocoloSsv, MARGEM, y);
    y += 12;

    y = this.desenharTitulo(doc, 'Dados da solicitação', y);
    doc.setFontSize(10);

    for (const linha of linhasComprovanteSolicitacao(resposta)) {
      doc.setTextColor(17, 24, 39);
      doc.setFont('helvetica', 'bold');
      doc.text(`${linha.rotulo}:`, MARGEM, y);

      doc.setTextColor(75, 85, 99);
      doc.setFont('helvetica', 'normal');

      for (const parte of doc.splitTextToSize(linha.valor, larguraUtil - LARGURA_ROTULO) as string[]) {
        doc.text(parte, MARGEM + LARGURA_ROTULO, y);
        y += ALTURA_LINHA;
      }
    }

    y = this.desenharTitulo(doc, 'Como acompanhar', y + 6);
    doc.setTextColor(75, 85, 99);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    const comoAcompanhar = resposta.bitAnonimaSsv
      ? 'Com este protocolo você acompanha o andamento da solicitação em "Acompanhamento de ' +
        'solicitação" no portal. Guarde-o: a solicitação é anônima e o protocolo é a única forma de ' +
        'encontrá-la.'
      : 'Com este protocolo você acompanha o andamento da solicitação em "Acompanhamento de ' +
        'solicitação" no portal. Guarde-o: é por ele que a solicitação é consultada.';

    for (const parte of doc.splitTextToSize(comoAcompanhar, larguraUtil) as string[]) {
      doc.text(parte, MARGEM, y);
      y += ALTURA_LINHA;
    }

    doc.setTextColor(107, 114, 128);
    doc.setFontSize(8);
    doc.text(`Emitido em ${dataHora(new Date().toISOString())}.`, MARGEM, RODAPE);
  }

  private desenharTitulo(doc: jsPDF, texto: string, y: number): number {
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(texto.toUpperCase(), MARGEM, y);

    return y + 8;
  }
}

function formatarDataIso(iso: string): string {
  const [ano, mes, dia] = iso.split('-');

  return `${dia}/${mes}/${ano}`;
}

function enderecoEmLinha(dados: {
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
}): string {
  const rua = [dados.endereco, dados.numero, dados.complemento].map((parte) => parte.trim()).filter(Boolean).join(', ');
  const cidadeUf = [dados.cidade.trim(), dados.uf].filter(Boolean).join('/');

  return [rua, dados.bairro.trim(), cidadeUf, dados.cep ? `CEP ${dados.cep}` : '']
    .filter(Boolean)
    .join(' - ');
}
